import { Ability } from '@casl/ability';
import {
  Actions,
  LocationFormBulk,
  ResourceBulkForm,
  Subjects,
} from '@dispatch/shared/types';
import AddIcon from '@mui/icons-material/Add';
import {
  Button,
  Grid,
  IconButton,
  List,
  MenuItem,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { useCallback, useState } from 'react';
import { UseFormWatch } from 'react-hook-form';
import { Close } from '@mui/icons-material';
import { ConfirmModal, Section } from '@dispatch/web/ui';

export type LocationSidebarProps = {
  selected: number;
  setSelected: (selected: number) => void;
  currentLocation: number;
  setLocation: (currentLocation: number) => void;
  permissions: Ability<[Actions, Subjects]> | undefined;
  watch: UseFormWatch<ResourceBulkForm>;
  addLocation: () => void;
  onDeleteLocation: (index: number) => void;
};

export function LocationSidebarBulk({
  selected,
  setSelected,
  currentLocation,
  setLocation,
  permissions,
  watch,
  onDeleteLocation,
  addLocation,
}: LocationSidebarProps) {
  const hasUpsertPermissions = permissions?.can?.('upsert', 'resource');
  const [toDelete, setToDelete] = useState<number | null>(null);
  const locations = watch('locations');
  const deleteLocation = (index: number) => {
    if (
      currentLocation > index ||
      (locations && currentLocation === locations.length - 1)
    ) {
      selectLocation(currentLocation - 1);
    }
  };

  const selectLocation = (index: number) => {
    setLocation(index);
    setSelected(index);
  };

  const renderLocationsList = useCallback(() => {
    return locations?.map((location: LocationFormBulk, index: number) => {
      return (
        <MenuItem
          key={index}
          selected={selected === index}
          onClick={() => selectLocation(index)}
          sx={{
            padding: '10px 5px',
            borderRadius: 1,
            position: 'relative',
            '&:hover': {
              backgroundColor: 'action.hover',
            },
            '&:hover .delete-button': {
              opacity: 1,
            },
            '&.Mui-selected': {
              backgroundColor: 'rgba(0, 0, 0, 0.04)',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.08)',
              },
            },
          }}
        >
          <IconButton
            className="delete-button"
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              setToDelete(index);
            }}
            sx={{
              position: 'absolute',
              top: '50%',
              right: 4,
              transform: 'translateY(-50%)',
              opacity: 0,
              transition: 'opacity 0.2s',
              p: 0.5,
              '&:hover': {
                backgroundColor: 'error.light',
                color: 'error.contrastText',
              },
            }}
          >
            <Close sx={{ fontSize: 16 }} />
          </IconButton>

          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{ width: '100%', pr: 3 }}
          >
            <Tooltip
              title={
                (location?.locationInformation?.name?.length || 0) > 13
                  ? location?.locationInformation?.name || ''
                  : ''
              }
              arrow
            >
              <Typography
                variant="body2"
                sx={{
                  fontWeight: selected === index ? 600 : 400,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  textAlign: 'left',
                  flex: 1,
                  minWidth: 0,
                }}
                data-testid={`location_${location?.locationInformation?.name}`}
              >
                {location?.locationInformation?.name}
              </Typography>
            </Tooltip>
          </Stack>
        </MenuItem>
      );
    });
  }, [hasUpsertPermissions, locations, selectLocation, selected]);

  const onDeleteConfirm = () => {
    if (toDelete != null) {
      onDeleteLocation(toDelete);
      deleteLocation(toDelete);
      setToDelete(null);
    }
  };

  return (
    <Section
      title="Locations"
      rightTopControl={
        hasUpsertPermissions
          ? () => (
              <Button
                variant="text"
                size="small"
                data-testid="add-location"
                sx={{
                  fontSize: '10px',
                  margin: 0,
                  padding: 0,
                }}
                onClick={() => {
                  addLocation();
                }}
              >
                <AddIcon sx={{ fontSize: '10px' }} /> Add Location
              </Button>
            )
          : undefined
      }
    >
      <Grid item>
        <List>{renderLocationsList()}</List>
        {toDelete != null && toDelete >= 0 && (
          <ConfirmModal
            onConfirm={onDeleteConfirm}
            onDeny={() => setToDelete(null)}
            confirmContent="Are you sure you want to delete this location?"
            confirmTitle="Delete Confirmation"
            modalType="delete"
          />
        )}
      </Grid>
    </Section>
  );
}
